import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { takeUntil } from 'rxjs';
import { BaseComponent } from '../../../../core/components/base/base.component';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { PatientService } from '../../../../core/services/patient.service';
import { PatientTaskService } from './../../../../core/services/patient-task.service';
import { TaskService } from './../../../../core/services/task.service';
import { UserService } from './../../../../core/services/user.service';
import { isTaskForUser, taskAssigneeSummary } from './../../../../core/utils/task-assignees';

/** Per-viewer convenience only (which way the toggle was left); never state that must persist. */
const SCOPE_KEY = 'task-list-scope';
export type TaskListScope = 'mine' | 'all';

@Component({
  selector: 'app-task-list',
  templateUrl: './task-list.component.html',
  styleUrls: ['./task-list.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({
        height: '0',
        overflow: 'hidden',
        opacity: '0'
      })),
      state('expanded', style({
        height: '*',
        opacity: '1'
      })),
      transition('expanded <=> collapsed', animate('300ms ease-out')),
    ])
  ]
})
export class TaskListComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() filterValue: string = '';
  tasks: any[] = [];
  loading = true;
  statusOptions: any[] = ['Pending', 'Completed', 'Cancelled'];
  filteredTasks: any[] = [];
  filteredTasksByStatus: any[] = [];


  public expandedStatus: string | null = 'Pending';

  /**
   * "Mine" = assigned to nobody (the whole practice) or to me — the same rule as the tab count and the
   * "Pending Tasks for Today" popup (`core/utils/task-assignees.ts`). `tasks/task-assignment.md`.
   */
  scope: TaskListScope = 'mine';
  private currentUserId: string | null = null;
  readonly assigneeSummary = taskAssigneeSummary;



  constructor(
    private patientService: PatientService,
    private patientTaskService: PatientTaskService,
    private taskService: TaskService,
    private userService: UserService,
    private router: Router,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    try {
      const saved = localStorage.getItem(SCOPE_KEY);
      if (saved === 'mine' || saved === 'all') this.scope = saved;
    } catch { /* storage unavailable: keep the default */ }

    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUserId = user?.id ?? null;
        this.applyFilter();
      });

    this.taskService.tasks$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(tasks => {
        // null while the practice's list is loading.
        this.loading = tasks === null;
        this.tasks = tasks || [];
        this.applyFilter();
      });

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filterValue']) {
      this.applyFilter();
    }
  }

  applyFilter() {
    if (!this.tasks) {
      return;
    }
    const filterTerms = this.filterValue.toLowerCase().split(' ');
    this.filteredTasks = _.filter(this.tasks, task => {
      if (this.scope === 'mine' && !isTaskForUser(task, this.currentUserId)) {
        return false;
      }

      const icdCodesMatch = task.Patient.icdCodes?.some((icdCode: any) =>
        filterTerms.every(term =>
          _.includes((icdCode.code || '').toLowerCase(), term) ||
          _.includes((icdCode.description || '').toLowerCase(), term)
        )
      );

      const patientMatch = filterTerms.every(term =>
        _.includes(task.Patient.name?.toLowerCase(), term) ||
        _.includes(task.Patient.surname?.toLowerCase(), term) ||
        _.includes(task.Patient.patientNumber?.toLowerCase(), term) ||
        _.includes(task.Patient.diagnosis?.toLowerCase(), term) ||
        _.includes(task.task?.toLowerCase(), term) ||
        _.includes(task.notes?.toLowerCase(), term) ||
        _.includes(taskAssigneeSummary(task).toLowerCase(), term)
      );

      return icdCodesMatch || patientMatch;
    }

    );

    this.filteredTasksByStatus = _.mapValues(
      _.groupBy(this.filteredTasks, 'status'),
      tasks => _.orderBy(tasks, ['surname', 'name'], ['asc', 'asc'])
    ) as any;
  }

  setScope(scope: TaskListScope) {
    // A click on the chip that is already selected sends no value; the scope stays as it is.
    if (!scope) return;
    this.scope = scope;
    try {
      localStorage.setItem(SCOPE_KEY, scope);
    } catch { /* storage unavailable: the choice lasts for this view only */ }
    this.applyFilter();
  }

  toggleStatusOption(option: string) {
    this.expandedStatus = this.expandedStatus === option ? null : option;
  }

  loadTask(task: any) {

    this.patientService.getPatientById(task.patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.patientTaskService.setEditingTask(task);
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['edit-task', task.id],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the task for editing. Please try again.');
      });
  }

  async updateTaskStatus(event: MouseEvent, task: any, status: string) {
    event.preventDefault();
    event.stopPropagation();

    const id = task?.id;
    if (!id) {
      return;
    }

    // Completed and Cancelled both drop the task out of the active list, so a stray click
    // silently loses work that another staff member was waiting on (support request,
    // Dr Makda, 2026-07-24 — accidental clicks while scrolling on a laptop). Marking a task
    // back to Pending is reversible and harmless, so it stays a single click.
    if (status === 'Completed' || status === 'Cancelled') {
      // The task text is passed through the dialog's `data` list so the user can see which
      // task they are about to change — the reported failure is hitting the wrong row, which
      // a message that named no task would not help them catch.
      const confirmed = await this.showConfirmationDialog(
        'Confirm Task Status',
        `Are you sure you want to mark this task as ${status}?`,
        task?.task ? [{ patient: task.task }] : undefined,
        'No',
        'Yes'
      );

      if (!confirmed) {
        return;
      }
    }

    this.patientTaskService.updateTask(id, { id, status })
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (result) => {
          this.showToastMessage('Task status updated successfully');
        },
        error: (error) => {
          console.error('Error updating task status: ', error);
          this.showToastMessage('There was an error updating the task status. Please try again.');
        }
      });

  }

  navigateToPatientFile(event: MouseEvent, patientId: string) {

    event.preventDefault();
    event.stopPropagation();

    this.patientService.getPatientById(patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['file', patientId],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the patient file. Please try again.');
      });
  }
}